import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, AlertCircle } from 'lucide-react';
import api from '../services/api';

const OAuthCallback: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const handleCallback = async () => {
            try {
                // Get authorization code and state from URL
                const code = searchParams.get('code');
                const state = searchParams.get('state');
                const error_param = searchParams.get('error');
                const error_description = searchParams.get('error_description');

                // Check for errors from OAuth provider
                if (error_param) {
                    setError(error_description || `OAuth error: ${error_param}`);
                    setTimeout(() => navigate('/login'), 3000);
                    return;
                }

                if (!code || !state) {
                    setError('Invalid OAuth callback parameters');
                    setTimeout(() => navigate('/login'), 3000);
                    return;
                }

                // Verify state (CSRF protection)
                const storedState = sessionStorage.getItem('oauth_state');
                if (state !== storedState) {
                    setError('Invalid state parameter. Possible CSRF attack.');
                    setTimeout(() => navigate('/login'), 3000);
                    return;
                }

                // Clear stored state
                sessionStorage.removeItem('oauth_state');

                // Exchange code for tokens via backend
                // Token and user are stored automatically by api.handleOAuthCallback()
                await api.handleOAuthCallback(code, state);

                // Redirect to dashboard (force reload to let AuthContext detect the token)
                window.location.href = '/dashboard';

            } catch (err: any) {
                console.error('OAuth callback error:', err);
                setError(err.response?.data?.detail || 'Authentication failed');
                setTimeout(() => navigate('/login'), 3000);
            }
        };

        handleCallback();
    }, [searchParams, navigate]);

    if (error) {
        return (
            <div className="min-h-screen bg-background-dark flex items-center justify-center p-4">
                <div className="max-w-md w-full bg-surface-dark border border-slate-800 rounded-2xl p-8">
                    <div className="text-center">
                        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-900/30 border border-red-800 mb-4">
                            <AlertCircle className="w-8 h-8 text-red-400" />
                        </div>
                        <h2 className="text-2xl font-bold text-white mb-2">Authentication Failed</h2>
                        <p className="text-slate-400 mb-6">{error}</p>
                        <p className="text-sm text-slate-500">Redirecting to login page...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background-dark flex items-center justify-center p-4">
            <div className="max-w-md w-full bg-surface-dark border border-slate-800 rounded-2xl p-8">
                <div className="text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 border border-primary/30 mb-4">
                        <Loader2 className="w-8 h-8 text-primary animate-spin" />
                    </div>
                    <h2 className="text-2xl font-bold text-white mb-2">Completing Sign In</h2>
                    <p className="text-slate-400">Please wait while we authenticate your account...</p>
                </div>
            </div>
        </div>
    );
};

export default OAuthCallback;
